import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { updateCompanyProfile, uploadCompanyProfileImage, fetchCompanyProfile } from '../../Redux/companySlice';
import { FaSpinner, FaCamera } from 'react-icons/fa';
import imageCompression from 'browser-image-compression';

const EditCompanyProfile = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { company, loading, error } = useSelector((state) => state.company);
  const [imageLoading, setImageLoading] = useState(false);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    companyName: '',
    companyWebsite: '',
    phone: '',
    location: '',
    industryType: '',
    companyStage: '',
    companySize: '',
    description: '',
  });

  useEffect(() => {
    if (company) {
      setFormData({
        firstName: company.firstName || '',
        lastName: company.lastName || '',
        companyName: company.companyName || '',
        companyWebsite: company.companyWebsite || '',
        phone: company.phone || '',
        location: company.location || '',
        industryType: company.industryType || '',
        companyStage: company.companyStage || '',
        companySize: company.companySize || '',
        description: company.description || '',
        profileImage: company.profileImage?.url || null
      });
    }
  }, [company]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file type
    if (!file.type.startsWith('image/')) {
      alert('Please upload an image file');
      return;
    }

    try {
      setImageLoading(true);
      
      // Compress the image with optimized settings
      const options = {
        maxSizeMB: 0.5,
        maxWidthOrHeight: 800,
        useWebWorker: true,
        initialQuality: 0.8
      };
      
      const compressedFile = await imageCompression(file, options);
      
      // Convert compressed file to base64
      const reader = new FileReader();
      reader.readAsDataURL(compressedFile);
      reader.onloadend = async () => {
        try {
          const response = await dispatch(uploadCompanyProfileImage(reader.result)).unwrap();
          
          if (response.success && response.profileImage) {
            // Update local state with new image URL
            setFormData(prev => ({
              ...prev,
              profileImage: response.profileImage.url
            }));
            
            // Refresh company data
            const profileResponse = await dispatch(fetchCompanyProfile()).unwrap();
            if (profileResponse.success) {
              // If we're still on the edit page, update the form data
              setFormData(prev => ({
                ...prev,
                ...profileResponse.company
              }));
            }
          }
        } catch (error) {
          console.error('Error uploading image:', error);
          alert('Failed to upload image. Please try again.');
        } finally {
          setImageLoading(false);
        }
      };
    } catch (error) {
      console.error('Error processing image:', error);
      setImageLoading(false);
      alert('Error processing image. Please try again.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Compare current form data with company data to find changes
      const changes = {};
      let hasChanges = false;

      // Check each field for changes
      if (formData.firstName !== company.firstName) {
        changes.firstName = formData.firstName;
        hasChanges = true;
      }
      if (formData.lastName !== company.lastName) {
        changes.lastName = formData.lastName;
        hasChanges = true;
      }
      if (formData.companyName !== company.companyName) {
        changes.companyName = formData.companyName;
        hasChanges = true;
      }
      if (formData.companyWebsite !== company.companyWebsite) {
        changes.companyWebsite = formData.companyWebsite;
        hasChanges = true;
      }
      if (formData.phone !== company.phone) {
        changes.phone = formData.phone;
        hasChanges = true;
      }
      if (formData.location !== company.location) {
        changes.location = formData.location;
        hasChanges = true;
      }
      if (formData.industryType !== company.industryType) {
        changes.industryType = formData.industryType;
        hasChanges = true;
      }
      if (formData.companyStage !== company.companyStage) {
        changes.companyStage = formData.companyStage;
        hasChanges = true;
      }
      if (formData.companySize !== company.companySize) {
        changes.companySize = formData.companySize;
        hasChanges = true;
      }
      if (formData.description !== company.description) {
        changes.description = formData.description;
        hasChanges = true;
      }

      // If no changes were made, just navigate to profile
      if (!hasChanges) {
        navigate('/company/profile');
        return;
      }

      // Only update if there are changes
      await dispatch(updateCompanyProfile(changes)).unwrap();
      // Refresh company data after successful update
      await dispatch(fetchCompanyProfile());
      navigate('/company/profile');
    } catch (err) {
      console.error('Failed to update profile:', err);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <FaSpinner className="animate-spin text-4xl text-orange-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <div className="flex-grow container mx-auto p-2 md:p-4 lg:p-6">
        <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-6">
          Edit Company Profile
        </h2>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="bg-white p-2 md:p-4 lg:p-6 rounded-xl shadow-lg space-y-5">
          <div className="flex justify-center mb-6">
            <div className="relative">
              <div className="h-24 w-24 rounded-full overflow-hidden border-4 border-orange-500 md:h-32 md:w-32">
                {company?.profileImage?.url ? (
                  <img
                    src={company.profileImage.url}
                    alt="Profile"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-orange-100 flex items-center justify-center text-2xl md:text-4xl font-bold text-orange-500">
                    {company?.companyName?.charAt(0).toUpperCase()}
                  </div>
                )}
              </div>
              <label
                htmlFor="profile-image"
                className="absolute bottom-0 right-0 bg-orange-500 text-white p-1 md:p-2 rounded-full cursor-pointer hover:bg-orange-600 transition-colors"
              >
                <FaCamera />
                <input
                  type="file"
                  id="profile-image"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                  disabled={imageLoading}
                />
              </label>
              {imageLoading && (
                <div className="absolute inset-0 bg-black bg-opacity-50 rounded-full flex items-center justify-center">
                  <FaSpinner className="animate-spin text-white text-xl md:text-2xl" />
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs md:text-sm font-medium text-gray-700 mb-2">
                First Name
              </label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                className="w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                required
              />
            </div>

            <div>
              <label className="block text-xs md:text-sm font-medium text-gray-700 mb-2">
                Last Name
              </label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                className="w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                required
              />
            </div>

            <div>
              <label className="block text-xs md:text-sm font-medium text-gray-700 mb-2">
                Company Name
              </label>
              <input
                type="text"
                name="companyName"
                value={formData.companyName}
                onChange={handleChange}
                className="w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                required
              />
            </div>

            <div>
              <label className="block text-xs md:text-sm font-medium text-gray-700 mb-2">
                Company Website
              </label>
              <input
                type="url"
                name="companyWebsite"
                value={formData.companyWebsite}
                onChange={handleChange}
                className="w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                required
              />
            </div>

            <div>
              <label className="block text-xs md:text-sm font-medium text-gray-700 mb-2">
                Phone Number
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              />
            </div>

            <div>
              <label className="block text-xs md:text-sm font-medium text-gray-700 mb-2">
                Location
              </label>
              <input
                type="text"
                name="location"
                value={formData.location}
                onChange={handleChange}
                className="w-full text-xs md:text-sm px-4 py-2 border rounded-md focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              />
            </div>

            <div>
              <label className="block text-xs md:text-sm font-medium text-gray-700 mb-2">
                Industry
              </label>
              <input
                type="text"
                name="industryType"
                value={formData.industryType}
                onChange={handleChange}
                className="w-full text-xs md:text-sm px-4 py-2 border rounded-md focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              />
            </div>

            <div>
              <label className="block text-xs md:text-sm font-medium text-gray-700 mb-2">
                Company Stage
              </label>
              <select
                name="companyStage"
                value={formData.companyStage}
                onChange={handleChange}
                className="w-full text-xs md:text-sm px-4 py-2 border rounded-md focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              >
                <option value="">Select Company Stage</option>
                <option value="startup">Startup</option>
                <option value="mid-size">Mid-size</option>
                <option value="enterprise">Enterprise</option>
              </select>
            </div>

            <div>
              <label className="block text-xs md:text-sm font-medium text-gray-700 mb-2">
                Company Size
              </label>
              <select
                name="companySize"
                value={formData.companySize}
                onChange={handleChange}
                className="w-full text-xs md:text-sm px-4 py-2 border rounded-md focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              >
                <option value="">Select Company Size</option>
                <option value="1-10">1-10</option>
                <option value="11-50">11-50</option>
                <option value="51-200">51-200</option>
                <option value="201-500">201-500</option>
                <option value="501+">501+</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs md:text-sm font-medium text-gray-700 mb-2">
              Company Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="4"
              className="w-full text-xs md:text-sm px-4 py-2 border rounded-md focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            />
          </div>

          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => navigate('/company/profile')}
              className="px-4 py-2 md:px-6 md:py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 md:px-6 md:py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <span className="flex items-center">
                  <FaSpinner className="animate-spin mr-2" />
                  Saving...
                </span>
              ) : (
                'Save Changes'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditCompanyProfile;